/* iter55z-audit round 9 (extras 7) — Missed-Search backlog widget.
 *
 * Mounted inside Admin → Financials → COGS Analyzer alongside the
 * other round-9 dashboards. Pulls the top 20 zero-hit queries over
 * the last 30 days and renders them as a click-to-copy backlog the
 * team can turn into new FAQ entries.
 *
 * Backend: GET /api/admin/search/misses?days=30
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, SearchX, Copy, RefreshCw, Sparkles } from 'lucide-react';

const API  = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

const copy = async (t) => {
  try { await navigator.clipboard.writeText(t); toast.success('Copied.'); }
  catch { toast.error('Copy failed.'); }
};

export default function MissedSearchPanel() {
  const [days, setDays] = useState(30);
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  // iter55z-audit round 9 (extras 8) — Track per-row "converting" state
  // so we can flip the button label + show a spinner without blocking
  // the rest of the list.
  const [converting, setConverting] = useState({});

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await http.get(`/admin/search/misses?days=${days}`);
      setRows(r.data.rows || []);
    } catch (e) {
      toast.error(`Load failed: ${e.response?.data?.detail || e.message}`);
      setRows([]);
    } finally { setBusy(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  // iter55z-audit round 9 (extras 8) — One-click convert-to-FAQ.
  const convert = async (q, i) => {
    setConverting((s) => ({ ...s, [i]: true }));
    try {
      const r = await http.post('/admin/faq/prefill-from-search', { q });
      if (r.data?.created) {
        const ai = r.data.ai_generated
          ? 'AI-drafted answer ready — review and approve.'
          : 'Placeholder answer added — draft the response.';
        toast.success(`New FAQ #${r.data.index + 1} appended. ${ai}`);
      } else {
        toast.info(r.data?.message || 'FAQ entry already existed — no change.');
      }
    } catch (e) {
      toast.error(`Convert failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setConverting((s) => ({ ...s, [i]: false }));
    }
  };

  return (
    <section data-testid="admin-missed-search-panel"
             className="rounded-md border border-border bg-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          <SearchX className="h-3.5 w-3.5" /> Missed searches — FAQ backlog
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))}
            data-testid="missed-search-window"
            className="h-7 rounded-sm border border-border bg-background px-1 text-[11.5px]"
          >
            <option value={7}>7d</option>
            <option value={30}>30d</option>
            <option value={90}>90d</option>
          </select>
          <button onClick={load} disabled={busy}
                  data-testid="missed-search-refresh"
                  className="rounded-sm bg-secondary p-1 hover:bg-secondary/80">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          </button>
        </div>
      </div>

      {rows === null ? (
        <div className="flex h-16 items-center justify-center"><Loader2 className="h-4 w-4 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-[12px] text-emerald-800 dark:text-emerald-300"
             data-testid="missed-search-empty">
          No zero-hit searches in the last {days} days — every query surfaced at least one result.
        </div>
      ) : (
        <>
          <div className="mb-2 text-[11px] text-muted-foreground">
            {rows.length} distinct queries returned no results. Each should probably
            become an FAQ entry or trigger a bookstore listing.
          </div>
          <ul className="space-y-1" data-testid="missed-search-list">
            {rows.map((r, i) => (
              <li key={i}
                  data-testid={`missed-search-row-${i}`}
                  className="flex items-center gap-2 rounded-sm border border-border/60 px-2 py-1 text-[12px]">
                <span className="w-6 font-mono text-muted-foreground">{i + 1}.</span>
                <span className="flex-1 truncate font-mono text-foreground" title={r.q}>{r.q}</span>
                <span className="rounded-full bg-secondary px-2 py-0.5 font-mono text-[10.5px]">
                  {r.count}× searched
                </span>
                <button onClick={() => copy(r.q)}
                        data-testid={`missed-search-copy-${i}`}
                        title="Copy query"
                        className="rounded-sm p-1 hover:bg-secondary">
                  <Copy className="h-3 w-3" />
                </button>
                <button
                  onClick={() => convert(r.q, i)}
                  disabled={!!converting[i]}
                  data-testid={`missed-search-convert-${i}`}
                  title="One-click: create a draft FAQ entry seeded from this query"
                  className="inline-flex items-center gap-1 rounded-sm bg-primary/90 px-1.5 py-0.5 text-[10.5px] font-semibold text-primary-foreground hover:bg-primary disabled:opacity-50"
                >
                  {converting[i]
                    ? <Loader2 className="h-3 w-3 animate-spin" />
                    : <Sparkles className="h-3 w-3" />}
                  Draft FAQ
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
